import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import { ItiString } from "@/iti-core-react/utils/iti-string";
import { ConsolidatedArtifactDto } from "@/shared/dtos/Gateway/consolidated-artifact-dto.ts";
import { CreateDatasetDto } from "@/shared/dtos/Gateway/create-dataset-dto.ts";
import { GovernedDatasetDto } from "@/shared/dtos/Gateway/governed-dataset-dto.ts";
import { UpdateDatasetDto } from "@/shared/dtos/Gateway/update-dataset-dto.ts";
import { CategoryDtoV2 } from "@/shared/dtos/Governance/category-dto-v2.ts";
import { PageDistributionDtoV2 } from "@/shared/dtos/Governance/page-distribution-dto-v2.ts";
import { PublishDatasetDto } from "@/shared/dtos/Governance/publish-dataset-dto";
import { SharingRecordDto } from "@/shared/dtos/Governance/sharing-record-dto";
import { ConsolidatedArtifactDto as PersistenceConsolidatedArtifactDto } from "@/shared/dtos/Persistence/consolidated-artifact-dto";
import { DatasetDto } from "@/shared/dtos/Persistence/dataset-dto";
import { DatasetStatusDto } from "@/shared/dtos/Persistence/dataset-status-dto";
import { API } from "../api";
import { CatalogueDatasetDto } from "../dtos/Governance/catalogue-dataset-dto";
import { DatasetDtoV2 } from "../dtos/Persistence/dataset-v2-dto";
import { ValidateImportAssetDto } from "../dtos/Sharing/validate-import-asset";
import { ArtifactExperimentDto } from "../dtos/artifact-experiment-dto";
import { DistributionType } from "../enums/distribution-type";
import { DatasetWithRelationsDtoV2 } from "@/shared/dtos/Governance/dataset-with-relations-dto-v2.ts";

export const DatasetService = {
	async getDataset(
		id: string,
		includeDistributions = true,
	): Promise<CatalogueDatasetDto> {
		const url = includeDistributions
			? `${API.governance.datasetsv2}/${id}`
			: `${API.governance.datasetsv2}/${id}?includeDistributions=false`;
		const res = await ItiHttpService.get(url);
		return new CatalogueDatasetDto(res);
	},
	async getDatasetById(
		id: string,
		includeDistributions = true,
	): Promise<DatasetWithRelationsDtoV2> {
		const url = includeDistributions
			? `${API.governance.datasetsv2}/${id}`
			: `${API.governance.datasetsv2}/${id}?includeDistributions=false`;
		return ItiHttpService.get(url);
	},
	async getDatasetDistributions(
		datasetId: string,
		distributionType = DistributionType.ALL,
		page = 0,
		size = 1000,
		sort = "ASC",
		sortBy = "TITLE",
		showLoading?: boolean,
	): Promise<PageDistributionDtoV2> {
		const queryParams = new URLSearchParams({
			page: page.toString(),
			size: size.toString(),
			sort,
			sortBy,
			type: distributionType,
		});
		return await ItiHttpService.get(
			`${API.governance.datasetsv2}/${datasetId}/distributions?${queryParams}`,
			{},
			showLoading ?? true,
		);
	},
	async delete(id: string): Promise<boolean> {
		return await ItiHttpService.delete(`${API.persistence.dataset}/${id}`);
	},
	async create(dataset: CreateDatasetDto): Promise<GovernedDatasetDto> {
		return ItiHttpService.post(`${API.gateway.datasets.datasets}`, dataset);
	},
	async createV2(dataset: DatasetDtoV2): Promise<DatasetDtoV2> {
		const res = await ItiHttpService.post(
			`${API.persistence.dataset}`,
			dataset.toJson(),
		);
		return new DatasetDtoV2(res);
	},
	async update(dataset: DatasetDto): Promise<DatasetDto> {
		return ItiHttpService.put(
			`${API.persistence.dataset}/${dataset.id}`,
			dataset,
		);
	},
	async updateV2(
		id: string,
		updateDataset: UpdateDatasetDto,
	): Promise<DatasetDtoV2> {
		const res = await ItiHttpService.put(
			`${API.gateway.datasets.datasets}/${id}`,
			updateDataset,
		);
		return new DatasetDtoV2(res);
	},
	async getDatasetArtifacts(): Promise<ArtifactExperimentDto[]> {
		const res = await ItiHttpService.get(
			`${API.centralizedAuth.resources}/datasets/all-has-permission/artifacts`,
		);
		return res.map(
			(item: ArtifactExperimentDto) => new ArtifactExperimentDto(item),
		);
	},
	async requestAccessToDataset(datasetId: any) {
		return await ItiHttpService.post(
			API.centralizedAuth.request.resources,
			JSON.stringify({
				resourceId: datasetId,
			}),
		);
	},
	async assignTermsFromDatasetV2(
		datasetId: string,
		termUids: string[],
	): Promise<void> {
		await ItiHttpService.post(
			`${API.governance.datasetsv2}/${datasetId}/terms`,
			[...new Set(termUids)],
		);
	},
	async removeTermsFromDatasetV2(
		datasetId: string,
		termId: string,
	): Promise<boolean> {
		return await ItiHttpService.delete(
			`${API.governance.datasetsv2}/${datasetId}/terms/${termId}`,
		);
	},
	async assignCategoriesToDatasetV2(
		datasetId: string,
		categories: CategoryDtoV2[],
	): Promise<void> {
		await ItiHttpService.post(
			`${API.governance.datasetsv2}/${datasetId}/categories`,
			[...new Set(categories.map((c) => c.id))],
		);
	},
	async removeCategoryFromDatasetV2(
		datasetId: string,
		category: CategoryDtoV2,
	): Promise<boolean> {
		return await ItiHttpService.delete(
			`${API.governance.datasetsv2}/${datasetId}/categories/${category.id}`,
		);
	},
	async removeCategoriesFromDatasetV2(
		datasetId: string,
		categories: CategoryDtoV2[],
	): Promise<boolean> {
		return await ItiHttpService.delete(
			`${API.governance.datasetsv2}/${datasetId}/categories`,
			[...new Set(categories.map((c) => c.id))],
		);
	},
	async publishToDataspace(
		datasetId: string,
		connectorId: string,
		showLoading = false,
	): Promise<SharingRecordDto> {
		const payload: PublishDatasetDto = { datasetId, connectorId };
		return ItiHttpService.post(
			API.governance.sharingRecord.sharingRecord,
			payload,
			showLoading,
			false,
		);
	},
	async consolidate(
		datasetId: string,
		artifactId: string,
		showLoading?: boolean,
	): Promise<ConsolidatedArtifactDto> {
		return ItiHttpService.post(
			`${API.gateway.consolidate}/${datasetId}/artifacts/${artifactId}/consolidate`,
			null,
			false,
			showLoading,
		);
	},
	async validateImportAsset(
		connectorId: string,
		assetId: string,
	): Promise<ValidateImportAssetDto> {
		return ItiHttpService.get(
			ItiString.format(
				API.governance.validateImportAsset,
				connectorId,
				assetId,
			),
		);
	},
	async importExternalDataset(
		connectorId: string,
		assetId: string,
	): Promise<void> {
		return ItiHttpService.post(
			ItiString.format(
				API.governance.importExternalDataset,
				connectorId,
				assetId,
			),
			null,
			false,
		);
	},
	async consolidateDataset(
		datasetId: string,
	): Promise<PersistenceConsolidatedArtifactDto[]> {
		return ItiHttpService.post(
			`${API.persistence.datasetPersist}/${datasetId}/consolidate`,
			null,
			false,
			false,
			false,
		);
	},
	async getDatasetStatus(
		datasetId: string,
		showLoading?: boolean,
	): Promise<DatasetStatusDto> {
		return ItiHttpService.get(
			`${API.persistence.datasetPersist}/${datasetId}/status`,
			undefined,
			showLoading,
		);
	},
};
